import * as T from "three";
import { texturePath, materialPath } from "./binary.js";
import { sheetFrame } from "./pcf.js";

const vertexShader = `
#include <common>
#include <fog_pars_vertex>
attribute vec3 center;
attribute vec3 tail;
attribute vec4 tint;
attribute vec2 sizeRotation;
attribute float yaw;
attribute vec4 rectA;
attribute vec4 rectB;
attribute float frameBlend;
uniform float trailMode;
uniform float orientation;
uniform vec3 cpRight;
uniform vec3 cpUp;
uniform vec3 cpForward;
uniform float orientToControlPoint;
uniform vec3 materialTint;
uniform float velocityRotation;
uniform float forwardAngle;
varying vec4 vTint;
varying vec2 vA;
varying vec2 vB;
varying float vBlend;
void main() {
    vec4 mvPosition = viewMatrix * vec4(center, 1.0);
    float rotation = sizeRotation.y;
    if (velocityRotation > 0.5 && trailMode < 0.5) {
        // For sprites, tail carries the world-space displacement. Apply the
        // camera rotation here, avoiding two CPU transforms per particle.
        vec2 motion = (viewMatrix * vec4(tail, 0.0)).xy;
        rotation = (dot(motion, motion) > 0.0 ? atan(motion.y, motion.x) : 0.0) + forwardAngle;
    }
    float c = cos(rotation), s = sin(rotation);
    vec2 corner = mat2(c, s, -s, c) * position.xy;
    if (trailMode > 0.5) {
        vec4 end = viewMatrix * vec4(tail, 1.0);
        vec2 axis = end.xy - mvPosition.xy;
        vec2 side = length(axis) > 0.00001 ? normalize(vec2(-axis.y, axis.x)) : vec2(1.0, 0.0);
        mvPosition = mix(mvPosition, end, uv.y);
        mvPosition.xy += side * position.x * sizeRotation.x;
    } else if (orientation > 1.5 && orientation < 2.5) {
        // SpriteCard ORIENTATION=2 uses (y1,x1), unlike camera-facing
        // sprites. With our flipped sheet V this is (corner.y,-corner.x).
        // Halo strips must face along the ring, not across it.
        vec3 offset = orientToControlPoint > 0.5 ? cpForward * corner.y + cpRight * corner.x : vec3(corner.y, -corner.x, 0.0);
        mvPosition += viewMatrix * vec4(offset * sizeRotation.x, 0.0);
    } else if (orientation > 0.5 && orientation < 1.5) {
        // Source Z-aligned sprites face the eye while keeping world Z upright.
        vec3 toParticle = center - cameraPosition;
        vec3 right = cross(vec3(0.0, 0.0, 1.0), toParticle);
        right = dot(right, right) > 0.000001 ? normalize(right) : vec3(1.0, 0.0, 0.0);
        right.xy = mat2(cos(yaw), -sin(yaw), sin(yaw), cos(yaw)) * right.xy;
        vec3 offset = -corner.x * right + vec3(0.0, 0.0, corner.y);
        mvPosition += viewMatrix * vec4(offset * sizeRotation.x, 0.0);
    } else if (orientation > 2.5) {
        vec3 offset = (cpRight * corner.x + cpUp * corner.y) * sizeRotation.x;
        mvPosition += viewMatrix * vec4(offset, 0.0);
    } else mvPosition.xyz += vec3(corner.x * cos(yaw), corner.y, -corner.x * sin(yaw)) * sizeRotation.x;
    gl_Position = projectionMatrix * mvPosition;
    vec3 color = max(tint.rgb, vec3(0.0));
    vec3 linearColor = mix(pow((color + 0.055) / 1.055, vec3(2.4)), color / 12.92, lessThanEqual(color, vec3(0.04045)));
    vTint = vec4(linearColor * materialTint, clamp(tint.a, 0.0, 1.0));
    // VTF sheet coordinates start at the top; quad Y points up.
    vec2 sheetUV = vec2(uv.x, 1.0 - uv.y);
    vA = mix(rectA.xy, rectA.zw, sheetUV);
    vB = mix(rectB.xy, rectB.zw, sheetUV);
    vBlend = frameBlend;
    #include <fog_vertex>
}`;
const fragmentShader = `
#include <common>
#include <fog_pars_fragment>
uniform sampler2D map;
uniform float alphaCutoff;
uniform float overbright;
varying vec4 vTint;
varying vec2 vA;
varying vec2 vB;
varying float vBlend;
void main() {
    vec4 texel = mix(texture2D(map, vA), texture2D(map, vB), vBlend);
    gl_FragColor = texel * vTint;
    gl_FragColor.rgb *= overbright;
    if (gl_FragColor.a <= alphaCutoff) discard;
    #include <fog_fragment>
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
}`;
const numeric = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;

/** Instanced world-space quads: one draw per PCF renderer, no object per particle. */
export class ParticleBatch {
    constructor(view, simulation, op, material, texture, root) {
        Object.assign(this, { view, simulation, op, texture });
        const capacity = simulation.capacity, geometry = this.geometry = new T.InstancedBufferGeometry();
        geometry.setAttribute("position", new T.Float32BufferAttribute([-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0], 3));
        geometry.setAttribute("uv", new T.Float32BufferAttribute([0, 0, 1, 0, 1, 1, 0, 1], 2));
        geometry.setIndex([0, 1, 2, 0, 2, 3]);
        this.attributes = {};
        // A non-sheet texture always uses the whole image. Constant vertex
        // attributes avoid allocating, packing and uploading nine floats/particle.
        const sheetAttributes = texture?.userData.particleSheet ? { rectA: 4, rectB: 4, frameBlend: 1 } : {};
        for (const [name, size] of Object.entries({ center: 3, tail: 3, tint: 4, sizeRotation: 2, yaw: 1, ...sheetAttributes })) {
            const attribute = new T.InstancedBufferAttribute(new Float32Array(capacity * size), size).setUsage(T.DynamicDrawUsage);
            geometry.setAttribute(name, attribute); this.attributes[name] = attribute;
        }
        this.sortedParticles = [];
        this.sortPosition = new T.Vector3();
        this.sortDistance = p => {
            const x = p.position[0] - this.sortPosition.x, y = p.position[1] - this.sortPosition.y, z = p.position[2] - this.sortPosition.z;
            return x * x + y * y + z * z;
        };
        this.byDistance = (a, b) => this.sortDistance(b) - this.sortDistance(a);
        const values = material.values;
        this.velocityRotation = simulation.definition.renderers.find(o => o.name === "render_screen_velocity_rotate");
        this.rope = op.name === "render_rope";
        this.trail = op.name === "render_sprite_trail" || this.rope;
        const additive = numeric(values.$additive) !== 0;
        const tint = String(values.$color2 ?? values.$color ?? "1 1 1").match(/[-\d.]+/g)?.map(Number) ?? [1, 1, 1];
        this.material = new T.ShaderMaterial({
            vertexShader, fragmentShader, transparent: true, depthWrite: false, depthTest: !numeric(values.$ignorez), side: T.DoubleSide,
            blending: additive ? T.AdditiveBlending : T.NormalBlending, fog: true,
            uniforms: { ...T.UniformsLib.fog, map: { value: texture }, alphaCutoff: { value: numeric(values.$alphatest) ? numeric(values.$alphatestreference, 0.5) : 0.001 },
                overbright: { value: numeric(values.$overbrightfactor, 1) }, trailMode: { value: this.trail ? 1 : 0 }, orientation: { value: numeric(op.orientation_type) },
                cpRight: { value: new T.Vector3(1, 0, 0) }, cpUp: { value: new T.Vector3(0, 0, 1) },
                cpForward: { value: new T.Vector3(1, 0, 0) }, orientToControlPoint: { value: 0 },
                materialTint: { value: new T.Vector3(...[0, 1, 2].map(i => tint[i] ?? 1)) },
                velocityRotation: { value: 0 }, forwardAngle: { value: 0 } },
        });
        Object.assign(this.material.defaultAttributeValues, { rectA: [0, 0, 1, 1], rectB: [0, 0, 1, 1], frameBlend: [0] });
        this.mesh = new T.Mesh(geometry, this.material); this.mesh.frustumCulled = true; root.add(this.mesh);
        if (material.shader !== "spritecard" && material.shader !== "unlitgeneric" && material.shader !== "particlesphere") view.diagnostic?.(`${simulation.definition.name}: approximating particle shader ${material.shader}`);
        if (numeric(values.$addself) || numeric(values.$dualsequence)) view.diagnostic?.(`${simulation.definition.name}: secondary SpriteCard texture layers are approximated`);
    }
    updateBounds(visibility, visibleClusters) {
        const bounds = this.bounds ??= new T.Box3();
        bounds.makeEmpty();
        const minLength = numeric(this.op["min length"]), maxLength = numeric(this.op["max length"], 2000);
        const fadeTime = Math.max(0.0001, numeric(this.op["length fade in time"]));
        const orientation = numeric(this.op.orientation_type), orientationCp = numeric(this.op["orientation control point"], -1);
        const cp = this.simulation.controlPoints.get(orientationCp >= 0 ? orientationCp : 0);
        let radiusScale = Math.SQRT2;
        if (!this.trail && cp?.basis && (orientation === 3 || orientation === 2 && orientationCp >= 0)) {
            const axes = orientation === 2 ? [0, 1] : [1, 2];
            radiusScale = Math.max(radiusScale, Math.hypot(...cp.basis[axes[0]]) + Math.hypot(...cp.basis[axes[1]]));
        }
        let minX = Infinity, minY = Infinity, minZ = Infinity,
            maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity, maxRadius = 0;
        for (const p of this.simulation.particles) {
            // The largest corner radius expands both heads and tails, including
            // a wide rope segment ending at a narrower neighboring particle.
            maxRadius = Math.max(maxRadius, p.radius);
            const [x, y, z] = p.position;
            minX = Math.min(minX, x); minY = Math.min(minY, y); minZ = Math.min(minZ, z);
            maxX = Math.max(maxX, x); maxY = Math.max(maxY, y); maxZ = Math.max(maxZ, z);
            if (this.trail && !this.rope) {
                const speed = Math.hypot(...p.velocity), fade = Math.min(1, (this.simulation.time - p.birth) / fadeTime);
                const length = Math.max(minLength, Math.min(maxLength, speed * p.trail * fade));
                const factor = length / Math.max(0.0001, speed),
                    tx = x - p.velocity[0] * factor, ty = y - p.velocity[1] * factor, tz = z - p.velocity[2] * factor;
                minX = Math.min(minX, tx); minY = Math.min(minY, ty); minZ = Math.min(minZ, tz);
                maxX = Math.max(maxX, tx); maxY = Math.max(maxY, ty); maxZ = Math.max(maxZ, tz);
            }
        }
        bounds.min.set(minX, minY, minZ); bounds.max.set(maxX, maxY, maxZ);
        bounds.expandByScalar(maxRadius * radiusScale);
        this.geometry.boundingSphere ??= new T.Sphere();
        bounds.getBoundingSphere(this.geometry.boundingSphere);
        if (bounds.isEmpty()) return false;
        if (!visibleClusters || !visibility?.clustersForBounds) return true;
        // Padded membership is conservative and avoids BSP walks while particles
        // move inside a previously queried region. Never cull from CP0 alone.
        if (this.boundsVisibility !== visibility || !this.pvsBounds?.containsBox(bounds)) {
            this.pvsBounds ??= new T.Box3();
            this.pvsBounds.copy(bounds).expandByScalar(32);
            this.boundsVisibility = visibility;
            this.clusters = visibility.clustersForBounds(this.pvsBounds.min.toArray(), this.pvsBounds.max.toArray());
        }
        return !this.clusters.length || this.clusters.some(cluster => visibleClusters.has(cluster));
    }
    update(camera, visibility, visibleClusters) {
        if (!this.updateBounds(visibility, visibleClusters)) {
            this.geometry.instanceCount = 0; this.mesh.visible = false; return;
        }
        const sim = this.simulation, orientationCp = numeric(this.op["orientation control point"], -1);
        // Keep the existing CP0 approximation for orientation 3; orientation 2
        // only uses a control-point plane when one is explicitly authored.
        const cp = orientationCp >= 0 ? sim.controlPoints.get(orientationCp) : numeric(this.op.orientation_type) === 3 ? sim.controlPoints.get(0) : null;
        let particles = sim.particles;
        this.material.uniforms.orientToControlPoint.value = cp?.basis ? 1 : 0;
        if (cp?.basis) { this.material.uniforms.cpForward.value.fromArray(cp.basis[0]); this.material.uniforms.cpRight.value.fromArray(cp.basis[1]); this.material.uniforms.cpUp.value.fromArray(cp.basis[2]); }
        if (sim.definition["Sort particles"] && this.material.blending !== T.AdditiveBlending && !this.rope && camera) {
            // Reuse sort storage; squared distance preserves the previous order
            // without temporary vectors/arrays or square roots in the comparator.
            this.sortPosition.copy(camera.position);
            const sorted = this.sortedParticles;
            sorted.length = particles.length;
            for (let i = 0; i < particles.length; i++) sorted[i] = particles[i];
            sorted.sort(this.byDistance); particles = sorted;
        }
        // Simulation keeps birth order, including after dead-particle compaction.
        // Rope adjacency therefore needs neither a copy nor an ID sort.
        const a = this.attributes, sheet = this.texture?.userData.particleSheet;
        const defaultSequence = sheet?.find(Boolean);
        const animationRate = numeric(this.op["animation rate"], 0.1), fps = this.op["use animation rate as FPS"];
        const fadeTime = Math.max(0.0001, numeric(this.op["length fade in time"]));
        const minLength = numeric(this.op["min length"]), maxLength = numeric(this.op["max length"], 2000);
        const forwardAngle = numeric(this.velocityRotation?.forward_angle, -90) * Math.PI / 180;
        this.material.uniforms.velocityRotation.value = this.velocityRotation && camera ? 1 : 0;
        this.material.uniforms.forwardAngle.value = forwardAngle;
        for (let i = 0; i < particles.length; i++) {
            const p = particles[i], age = sim.time - p.birth;
            a.center.array.set(p.position, i * 3);
            let tail = p.position;
            if (this.rope) tail = particles[i + 1]?.position ?? p.position;
            else if (this.trail) {
                const speed = Math.hypot(...p.velocity), fade = Math.min(1, age / fadeTime);
                const length = Math.max(minLength, Math.min(maxLength, speed * p.trail * fade));
                for (let j = 0; j < 3; j++) a.tail.array[i * 3 + j] = p.position[j] - p.velocity[j] / Math.max(0.0001, speed) * length;
            }
            if (this.rope) a.tail.array.set(tail, i * 3);
            else if (!this.trail && this.velocityRotation) for (let j = 0; j < 3; j++) a.tail.array[i * 3 + j] = p.position[j] - p.previous[j];
            a.tint.array.set(p.color, i * 4); a.tint.array[i * 4 + 3] = p.alpha;
            a.sizeRotation.array[i * 2] = Math.max(0, p.radius); a.sizeRotation.array[i * 2 + 1] = p.rotation;
            a.yaw.array[i] = p.yaw;
            if (a.rectA) {
                const sequence = sheet?.[p.sequence] ?? defaultSequence;
                const rate = animationRate * (fps ? 1 : (sequence?.duration ?? 1));
                const frame = sheetFrame(sheet, p.sequence, age, rate, !!this.op.animation_fit_lifetime, p.life);
                a.rectA.array.set(frame.a, i * 4); a.rectB.array.set(frame.b, i * 4); a.frameBlend.array[i] = frame.blend;
            }
        }
        if (particles.length) for (const attribute of Object.values(a)) {
            if (attribute === a.tail && !this.trail && !this.velocityRotation) continue;
            // Three uploads full capacity unless a range is supplied. Reset it
            // even if the previous frame was not drawn (e.g. a hidden scene).
            attribute.clearUpdateRanges();
            attribute.addUpdateRange(0, particles.length * attribute.itemSize);
            attribute.needsUpdate = true;
        }
        this.geometry.instanceCount = particles.length; this.mesh.visible = particles.length > 0;
    }
    dispose() { this.mesh.removeFromParent(); this.geometry.dispose(); this.material.dispose(); }
}

export async function loadParticleMaterial(view, rpc, definition) {
    const path = materialPath(definition.material || "vgui/white");
    const { material } = await rpc("material", { path });
    if (!material.values.$basetexture) throw new Error(`Particle material lacks a base texture: ${path}`);
    const texture = await view.texture(texturePath(material.values.$basetexture), rpc);
    return { material, texture };
}
